import CircularProgress from "@mui/material/CircularProgress";
import ProductCard from "@/components/products/ProductCard";
import type { Product } from "@/interfaces/product";
import BudgetChips from "./BudgetChips";

type Props = {
  lowerTiers: number[];
  selectedTier: number | null;
  onSelectTier: (amount: number) => void;
  isLoading: boolean;
  products: Product[];
  existingIds: Set<string>;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onProductClick: (id: string) => void;
};

export default function AddMoreView({
  lowerTiers,
  selectedTier,
  onSelectTier,
  isLoading,
  products,
  existingIds,
  selectedIds,
  onToggleSelect,
  onProductClick,
}: Props) {
  const availableProducts = products.filter((p) => !existingIds.has(p.id));

  return (
    <>
      {/* Price tier heading */}
      <div className="border-t-4 border-dark-dust mb-3 mx-5" />
      <div className="flex flex-col items-center gap-1 py-4">
        <p className="font-crimson text-[18px] text-rodeo-black m-0">
          Add new gifts from these price tiers
        </p>
        {lowerTiers.length === 0 ? (
          <p className="font-lato text-[14px] text-graphite text-center px-8 mt-2">
            No lower price tiers are available. Gifts can only be added from collections priced
            below your current tier.
          </p>
        ) : (
          <p className="font-lato text-[13px] text-rodeo-black">Choose a price point</p>
        )}
      </div>

      {/* Chips row */}
      {lowerTiers.length > 0 && (
        <BudgetChips
          budgetOptions={lowerTiers}
          selectedBudget={selectedTier}
          onSelect={onSelectTier}
          centered
        />
      )}

      {/* Add-more product grid — shown once a tier is picked */}
      {selectedTier !== null && (
        <div className="px-5 pb-5 border-t border-smoke">
          {isLoading ? (
            <div className="flex justify-center py-10">
              <CircularProgress
                aria-label="Loading gifts"
                size={32}
                sx={{ color: "var(--color-dark-dust)" }}
              />
            </div>
          ) : availableProducts.length === 0 ? (
            <p className="font-lato text-[14px] text-graphite text-center py-10">
              All gifts at this price point are already in your collection.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-6 pt-4">
              {availableProducts.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onClick={() => onProductClick(product.id)}
                  isAddMoreMode
                  isSelected={selectedIds.has(product.id)}
                  onToggleSelect={() => onToggleSelect(product.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
